import { useCallback, useRef } from "react";
import { useDataProvider, useGetIdentity } from "ra-core";
import type { Company } from "../types";

export type CompanyImportSchema = {
  name: string;
  domain?: string;
  website?: string;
  linkedin_url?: string;
  sector?: string;
  country?: string;
  city?: string;
  size?: number | string;
  description?: string;
  status?: string;
  source?: string;
};

export type DuplicateMode = "skip" | "update";

const keyFor = (company: { name: string; domain?: string | null }) =>
  company.domain?.trim().toLowerCase() || company.name.trim().toLowerCase();

export const useCompanyImport = (duplicateMode: DuplicateMode) => {
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const existingRef = useRef<Map<string, Company> | null>(null);

  return useCallback(
    async (batch: CompanyImportSchema[]) => {
      if (!existingRef.current) {
        const { data } = await dataProvider.getList<Company>("companies", {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: "id", order: "ASC" },
          filter: {},
        });
        existingRef.current = new Map(
          data.map((company) => [keyFor(company), company]),
        );
      }

      for (const row of batch) {
        const name = String(row.name ?? "").trim();
        if (!name) throw new Error("La columna name es obligatoria");

        const clean = Object.fromEntries(
          Object.entries({
            name,
            domain: row.domain
              ? String(row.domain).trim().toLowerCase()
              : undefined,
            website: row.website ? String(row.website).trim() : undefined,
            linkedin_url: row.linkedin_url
              ? String(row.linkedin_url).trim()
              : undefined,
            sector: row.sector ? String(row.sector).trim() : undefined,
            country: row.country ? String(row.country).trim() : undefined,
            city: row.city ? String(row.city).trim() : undefined,
            size: row.size ? Number(row.size) : undefined,
            description: row.description
              ? String(row.description).trim()
              : undefined,
            status: row.status ? String(row.status).trim() : "prospect",
            source: row.source ? String(row.source).trim() : "csv",
            sales_id: identity?.id,
          }).filter(([, value]) => value !== undefined && value !== ""),
        );

        const key = keyFor({
          name,
          domain: clean.domain as string | undefined,
        });
        const existing = existingRef.current.get(key);
        if (existing && duplicateMode === "skip") continue;

        if (existing) {
          const { data } = await dataProvider.update<Company>("companies", {
            id: existing.id,
            data: { ...existing, ...clean },
            previousData: existing,
          });
          existingRef.current.set(key, data);
        } else {
          const { data } = await dataProvider.create<Company>("companies", {
            data: {
              ...clean,
              created_at: new Date().toISOString(),
            } as Omit<Company, "id">,
          });
          existingRef.current.set(key, data);
        }
      }
    },
    [dataProvider, duplicateMode, identity?.id],
  );
};
